import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Twitter, MapPin, Mail } from "lucide-react";
import EncoreWordmark from "./EncoreWordmark";
import { PhotoFilmstrip } from "./PhotoElements";
import { SHOW_PHOTOS } from "../lib/photos";

export default function Footer() {
  return (
    <>
      {/* Filmstrip band flowing into footer */}
      <PhotoFilmstrip
        tone="duotone"
        speed={110}
        height="h-24 md:h-28"
        photos={SHOW_PHOTOS.slice().reverse()}
      />
      <footer
        data-testid="site-footer"
        className="bg-[#211a1c] text-[#FDFBF7] pt-20 pb-12 px-6 md:px-10 relative overflow-hidden"
      >
      <div className="absolute inset-x-0 top-0 marquee" />
      <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-12 mb-16">
        <div className="md:col-span-2">
          <EncoreWordmark size="hero" className="-ml-1" />
          <p className="text-stone-400 mt-6 max-w-md leading-relaxed">
            The Little Theatre with a lot of Soul! A Birmingham community
            theatre weaving the voices of the African diaspora into a
            tapestry of stories — since 2018.
          </p>
          <div className="flex gap-4 mt-8">
            <a
              data-testid="footer-instagram"
              href="https://instagram.com/encorebham"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center hover:border-[#DE9E48] hover:text-[#DE9E48] transition-colors"
            >
              <Instagram size={16} />
            </a>
            <a
              data-testid="footer-facebook"
              href="https://facebook.com/encorebham"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center hover:border-[#DE9E48] hover:text-[#DE9E48] transition-colors"
            >
              <Facebook size={16} />
            </a>
            <a
              data-testid="footer-twitter"
              href="https://twitter.com/encorebham"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center hover:border-[#DE9E48] hover:text-[#DE9E48] transition-colors"
            >
              <Twitter size={16} />
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-overline text-xs text-[#DE9E48] mb-5">Visit</h4>
          <p className="text-stone-300 leading-relaxed flex items-start gap-2">
            <MapPin size={16} className="mt-1 flex-shrink-0 text-[#DE9E48]" />
            <span>
              1604 2nd Avenue North
              <br />
              Birmingham, AL 35203
            </span>
          </p>
        </div>

        <div>
          <h4 className="font-overline text-xs text-[#DE9E48] mb-5">Explore</h4>
          <ul className="space-y-3 text-stone-300">
            <li>
              <Link
                to="/about"
                data-testid="footer-link-about"
                className="hover:text-[#DE9E48] transition-colors"
              >
                Our Story
              </Link>
            </li>
            <li>
              <Link
                to="/gallery"
                data-testid="footer-link-gallery"
                className="hover:text-[#DE9E48] transition-colors"
              >
                Gallery
              </Link>
            </li>
            <li>
              <Link
                to="/news"
                data-testid="footer-link-news"
                className="hover:text-[#DE9E48] transition-colors"
              >
                News
              </Link>
            </li>
            <li>
              <Link
                to="/booking"
                data-testid="footer-link-booking"
                className="hover:text-[#DE9E48] transition-colors"
              >
                Book Our Space
              </Link>
            </li>
            <li>
              <Link
                to="/history"
                data-testid="footer-link-history"
                className="hover:text-[#DE9E48] transition-colors"
              >
                Our History
              </Link>
            </li>
            <li>
              <Link
                to="/policies"
                data-testid="footer-link-policies"
                className="hover:text-[#DE9E48] transition-colors"
              >
                Policies &amp; Procedures
              </Link>
            </li>
            <li>
              <Link
                to="/contact"
                data-testid="footer-link-contact"
                className="hover:text-[#DE9E48] transition-colors flex items-center gap-2"
              >
                <Mail size={14} /> Contact Us
              </Link>
            </li>
            <li>
              <a
                data-testid="footer-link-donate"
                href="https://www.paypal.com/donate/?hosted_button_id=DJQ4S2EYTXKGS"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#DE9E48] transition-colors"
              >
                Donate ↗
              </a>
            </li>
            <li>
              <a
                data-testid="footer-link-tickets"
                href="https://encorebham.ludus.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#DE9E48] hover:underline"
              >
                Get Tickets ↗
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="max-w-7xl mx-auto border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-stone-500 font-overline">
        <p data-testid="footer-copyright">
          © {new Date().getFullYear()} Encore Theatre &amp; Gallery
        </p>
        <p className="italic font-display text-base normal-case tracking-normal text-stone-400">
          &ldquo;The Little Theatre with A Lot of Soul&rdquo;
        </p>
        <Link
          to="/admin/login"
          data-testid="footer-admin-link"
          className="hover:text-[#DE9E48] transition-colors"
        >
          Admin
        </Link>
      </div>
    </footer>
    </>
  );
}
